import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

import {
  carregarDicionario,
  tradutorDe,
  type DicionarioParcial,
  type Tradutor,
} from '@vestigo/core/i18n-da-tela';

import { api } from './api.js';
import { slugDoCaminho } from './endereco.js';

/**
 * ── A LÍNGUA DA SESSÃO, PARA A TELA ────────────────────────────────────────
 *
 * O cabo entre o motor puro (`@vestigo/core/i18n`) e os componentes. Ele não
 * decide nada: quem resolve a língua é o servidor (`resolverIdiomaDoAluno`),
 * e quem traduz é o motor. Aqui só se guarda o resultado.
 *
 * ## Por que um contexto, e não `document.documentElement.lang`
 *
 * O `lang` existe e é escrito, mas lê-se **fora do React**: mudá-lo não
 * repinta nada. Uma tela que se traduzisse a partir dele ficaria na língua
 * antiga até ao próximo render por outro motivo, e o sintoma seria metade da
 * página numa língua e metade noutra.
 *
 * ## UMA leitura por área — e a cerca que provou que eram duas
 *
 * O retrato inteiro mora aqui (a língua, a origem e as disponíveis), e não só
 * o código. A primeira escrita guardava só a língua, e deixava o seletor
 * (`EscolhaDoIdioma`) buscar a lista por conta própria: **dois pedidos da
 * mesma coisa em toda tela que desenha o cabeçalho**, que é toda tela da
 * área.
 *
 * Quem o apanhou foi `os-links-do-topo-chegam-a-toda-tela`, que conta os
 * pedidos do topo e falhou com um a mais. Sem ela, o custo teria subido para
 * produção sem nada acender — é exatamente o que um pedido pequeno e correto
 * faz.
 *
 * ## `null` enquanto não chega, e isso não é um ecrã de espera
 *
 * `null` é «ainda não sei», e é indistinguível, para quem traduz, de «esta
 * área é monolingue» — nos dois casos o motor devolve português, que é o
 * fallback. A tela desenha em português e passa à língua do aluno quando a
 * resposta chega.
 *
 * Bloquear até à resposta poria um «Carregando…» à frente de toda a área de
 * membros para servir um recurso que a maioria das áreas nem liga.
 */

export interface RetratoDoIdioma {
  /** A area a que este retrato pertence -- posta pelo provedor, que sabe o
      caminho; o servidor nao a manda porque quem perguntou ja a tinha. */
  areaSlug: string;
  atual: string;
  /** `escolha` | `nativo` | `padrao` — de que degrau da cadeia ela veio. */
  origem: string;
  disponiveis: { codigo: string; nome: string }[];
}

interface EstadoDoIdioma {
  retrato: RetratoDoIdioma | null;
  /** `null` enquanto o pedaço da língua não desceu — o motor cai no português. */
  dicionario: DicionarioParcial | null;
}

const Ctx = createContext<EstadoDoIdioma>({ retrato: null, dicionario: null });

export function ProvedorDeIdioma({ children }: { children: ReactNode }) {
  /*
   * O slug sai de `slugDoCaminho`, e não de `useParams`: este provedor envolve
   * a tabela de rotas, e `useParams()` só tem valor DENTRO de uma `<Route>`.
   * Ali ele devolveria `{}` sempre — no host próprio cairia no `AREA_DO_HOST`
   * por sorte, e no painel ficaria vazio, que é o modo de falhar silencioso
   * descrito em `endereco.ts`.
   *
   * Cadeia vazia nas telas que não são de área (a conta, a lista de áreas):
   * ali não há língua a resolver, e o motor devolve português.
   */
  const areaSlug = slugDoCaminho(useLocation().pathname);
  const [retrato, setRetrato] = useState<RetratoDoIdioma | null>(null);
  const [dicionario, setDicionario] = useState<DicionarioParcial | null>(null);

  useEffect(() => {
    if (!areaSlug) {
      setRetrato(null);
      return;
    }
    let vivo = true;
    /*
     * ── A SEGUNDA PORTA, PARA QUEM AINDA NÃO ENTROU (27/10/2026) ───────────
     *
     * `/a/:slug/idioma` exige MATRÍCULA, e as telas públicas desta área — o
     * cadastro, a promo, a prévia, a entrada — são telas de área sem sessão.
     * Medido em produção: ela responde **401** a quem chega de fora, e este
     * `.catch` engolia-o. O efeito era a primeira tela que um comprador
     * estrangeiro vê ser a única garantidamente em português, mesmo com a
     * área a ter espanhol ligado e as frases traduzidas nas seis línguas.
     *
     * A porta pública (`GET /entrada/:slug`) responde a qualquer um e passou a
     * trazer o mesmo retrato, resolvido sem escolha de membro — ver
     * `retratoPublicoDoIdioma`, no servidor. Ela NÃO substitui a primeira: com
     * sessão e matrícula, quem decide é a escolha da pessoa, e é por isso que
     * a pública só é tentada quando a primeira falha.
     *
     * Falhar as DUAS não acende nada, e isso continua de propósito: ficar em
     * português é o comportamento de sempre, e uma área que não responde sobre
     * idioma tem problemas maiores do que a tela não traduzir.
     */
    api
      .get<unknown>(`/a/${encodeURIComponent(areaSlug)}/idioma`)
      .then((r) => {
        if (vivo) setRetrato(lerRetrato(r, areaSlug));
      })
      .catch(() =>
        api
          .get<{ idioma?: unknown }>(`/entrada/${encodeURIComponent(areaSlug)}`)
          .then((r) => {
            if (vivo) setRetrato(lerRetrato(r?.idioma, areaSlug));
          })
          .catch(() => {}),
      );
    return () => {
      vivo = false;
    };
  }, [areaSlug]);

  /*
   * ── O DICIONÁRIO DA LÍNGUA, SOB DEMANDA ─────────────────────────────────
   *
   * Só o português vive no bundle; os outros cinco descem por `import()`
   * quando a área de facto os usa.
   *
   * A razão está medida: a primeira escrita importava os seis no topo do
   * motor, e a cerca `o-editor-nao-entra-na-porta` — que pesa o pedaço de
   * entrada — acusou **429.128 B contra um teto de 339.600**. Eram 87 kB de
   * dicionários que ninguém naquela página ia ler.
   *
   * Enquanto não chega, `null`: o motor devolve português, que é o fallback.
   * Uma área monolingue não baixa um byte a mais do que baixava antes desta
   * obra.
   */
  useEffect(() => {
    if (!retrato) {
      setDicionario(null);
      return;
    }
    let vivo = true;
    void carregarDicionario(retrato.atual).then((d) => {
      if (vivo) setDicionario(d);
    });
    return () => {
      vivo = false;
    };
  }, [retrato]);

  /*
   * ── O `lang` DO DOCUMENTO, QUE ATÉ HOJE MENTIA ──────────────────────────
   *
   * `apps/aluno/index.html` é estático e traz `lang="pt-BR"` cravado — medido
   * em produção com uma aula de áudio italiano a servir `pt-BR` (`D34-6`).
   *
   * Não é cosmético: é com este atributo que o leitor de ecrã escolhe a voz e
   * que o navegador decide se oferece tradução. Uma página em espanhol
   * declarada como portuguesa é lida com a pronúncia errada, palavra a
   * palavra.
   *
   * Mora aqui, e não no seletor, porque uma área com UMA língua habilitada
   * não mostra seletor nenhum — e continua a precisar que o `lang` diga a
   * verdade. Sendo o `index.html` estático, só se faz em runtime.
   */
  useEffect(() => {
    if (retrato) document.documentElement.lang = retrato.atual;
  }, [retrato]);

  return <Ctx.Provider value={{ retrato, dicionario }}>{children}</Ctx.Provider>;
}

/** O retrato da língua desta área, ou `null` — «monolingue» ou «ainda não sei». */
export function useRetratoDoIdioma(): RetratoDoIdioma | null {
  return useContext(Ctx).retrato;
}

/** A língua desta sessão, ou `null`. */
export function useIdioma(): string | null {
  return useContext(Ctx).retrato?.atual ?? null;
}

/**
 * ── O LOCALE DESTA SESSAO — para o `Intl`, e nao para o dicionario ─────────
 *
 * A `A8` pede *«datas e numeros por `Intl` com o locale resolvido»*, e isso
 * e uma coisa DIFERENTE do dicionario: o dicionario traduz as frases que nos
 * escrevemos, e o `Intl` traduz o que o sistema operativo sabe — o nome do
 * mes, a ordem de dia e mes, o separador decimal, a moeda.
 *
 * Um sem o outro produz a tela que delata a tradução pela metade: a frase em
 * espanhol e a data a dizer «12 de fevereiro de 2026».
 *
 * ## `pt-BR` quando nao ha lingua resolvida, e isso NAO e o mesmo que `undefined`
 *
 * Passar `undefined` ao `Intl` usa o locale do NAVEGADOR, que nao e o da
 * area: um aluno com o Chrome em ingles numa area so em portugues leria a
 * interface em portugues e as datas em ingles. A area e que manda, e quando
 * ela e monolingue a lingua dela e o portugues.
 */
export function useLocale(): string {
  return useContext(Ctx).retrato?.atual ?? 'pt-BR';
}

/**
 * O tradutor desta tela.
 *
 * `useMemo` no dicionário: o tradutor é uma closure, e recriá-lo a cada
 * render faria todo componente que o receba por prop repintar sem motivo.
 *
 * Antes de o pedaço da língua descer, o dicionário é `null` e o motor serve
 * português — a tela desenha e troca de língua quando ele chega, em vez de
 * ficar num «Carregando…» à frente de toda a área de membros.
 */
export function useT(): Tradutor {
  const { dicionario } = useContext(Ctx);
  return useMemo(() => tradutorDe(dicionario), [dicionario]);
}

/**
 * A resposta do servidor, conferida antes de virar estado.
 *
 * ## O defeito que esta função existe para não deixar acontecer
 *
 * A primeira escrita fazia `r ? { ...r, areaSlug } : null` — e um `{}` é
 * verdadeiro. Bastou um teste cujo mock devolve `{}` para rotas que não
 * conhece, e o seletor rebentou em `retrato.disponiveis.length`: duas cercas
 * de integração ficaram vermelhas com *«o convite do Instagram não abriu
 * direito»*, uma frase que não aponta para lado nenhum.
 *
 * Em produção o `{}` chega pelos mesmos caminhos de sempre — uma rota que
 * mudou de forma, um proxy que devolve vazio, uma versão antiga do servidor
 * durante um deploy. A tela do aluno não pode cair por isso.
 *
 * `null` é a resposta honesta para tudo o que não for um retrato inteiro: a
 * área comporta-se como monolingue, que é o que ela fazia antes desta obra.
 */
function lerRetrato(cru: unknown, areaSlug: string): RetratoDoIdioma | null {
  if (!cru || typeof cru !== 'object') return null;
  const o = cru as Record<string, unknown>;
  if (typeof o.atual !== 'string' || !o.atual) return null;
  if (!Array.isArray(o.disponiveis)) return null;

  /* Uma entrada estragada não leva as boas com ela — a lição de
     `salvar-o-que-vale`. */
  const disponiveis = o.disponiveis.flatMap((d) => {
    if (!d || typeof d !== 'object') return [];
    const i = d as Record<string, unknown>;
    if (typeof i.codigo !== 'string' || typeof i.nome !== 'string') return [];
    return [{ codigo: i.codigo, nome: i.nome }];
  });

  return {
    areaSlug,
    atual: o.atual,
    origem: typeof o.origem === 'string' ? o.origem : 'padrao',
    disponiveis,
  };
}
